import { forwardRef } from "react";
import { Link } from "react-router-dom";
import { ArrowLeft } from "lucide-react";
import Navbar from "@/components/Navbar";
import SEOHead from "@/components/SEOHead";

const breadcrumbLd = {
  "@context": "https://schema.org",
  "@type": "BreadcrumbList",
  itemListElement: [
    { "@type": "ListItem", position: 1, name: "Início", item: "https://indexrerum.com/" },
    { "@type": "ListItem", position: 2, name: "Política de Privacidade", item: "https://indexrerum.com/privacidade" },
  ],
};

const PrivacyPolicy = forwardRef<HTMLDivElement>((_, ref) => (
  <div ref={ref} className="min-h-screen bg-background">
    <SEOHead
      title="Política de Privacidade | Index Rerum"
      description="Consulte a política de privacidade da plataforma Index Rerum — tratamento de dados pessoais em conformidade com o RGPD."
      canonicalPath="/privacidade"
      jsonLd={breadcrumbLd}
    />
    <Navbar />
    <div className="container mx-auto px-4 py-16 pt-28 max-w-3xl">
      <h1 className="font-serif text-3xl md:text-4xl font-bold text-foreground mb-2">Política de Privacidade</h1>
      <p className="text-muted-foreground mb-10">Última atualização: março de 2026</p>

      <div className="prose prose-neutral max-w-none space-y-8 text-foreground/80">
        <section>
          <h2 className="font-serif text-xl font-bold text-foreground">1. Responsável pelo Tratamento</h2>
          <p>O responsável pelo tratamento dos dados pessoais recolhidos através da plataforma Index Rerum é a EVOKE IT, com sede em Portugal.</p>
        </section>

        <section>
          <h2 className="font-serif text-xl font-bold text-foreground">2. Dados Recolhidos</h2>
          <p>Recolhemos os seguintes dados pessoais quando utiliza a nossa plataforma ou formulário de contacto:</p>
          <ul className="list-disc pl-6 space-y-1">
            <li>Nome e apelido</li>
            <li>Endereço de email</li>
            <li>Número de telefone</li>
            <li>Nome da instituição</li>
            <li>Dados de utilização e navegação (cookies)</li>
          </ul>
        </section>

        <section>
          <h2 className="font-serif text-xl font-bold text-foreground">3. Finalidade do Tratamento</h2>
          <p>Os dados pessoais são tratados para as seguintes finalidades:</p>
          <ul className="list-disc pl-6 space-y-1">
            <li>Responder a pedidos de contacto e demonstração</li>
            <li>Gestão de contas de utilizador</li>
            <li>Melhoria dos nossos serviços e experiência de utilização</li>
            <li>Cumprimento de obrigações legais</li>
          </ul>
        </section>

        <section>
          <h2 className="font-serif text-xl font-bold text-foreground">4. Base Legal</h2>
          <p>O tratamento dos dados pessoais baseia-se no consentimento do titular, na execução de contrato e no cumprimento de obrigações legais, conforme o Regulamento Geral sobre a Proteção de Dados (RGPD).</p>
        </section>

        <section>
          <h2 className="font-serif text-xl font-bold text-foreground">5. Conservação dos Dados</h2>
          <p>Os dados pessoais são conservados durante o período necessário para as finalidades para que foram recolhidos, ou conforme exigido por lei.</p>
        </section>

        <section>
          <h2 className="font-serif text-xl font-bold text-foreground">6. Direitos do Titular</h2>
          <p>Nos termos do RGPD, tem direito a aceder, retificar, apagar, limitar o tratamento, portabilidade e oposição ao tratamento dos seus dados pessoais. Para exercer estes direitos, contacte-nos através do email disponível no nosso site.</p>
        </section>

        <section>
          <h2 className="font-serif text-xl font-bold text-foreground">7. Contacto</h2>
          <p>Para questões relacionadas com a privacidade dos seus dados, contacte a EVOKE IT através do formulário de contacto disponível em <Link to="/#contacto" className="text-gold hover:underline">indexrerum.pt</Link>.</p>
        </section>
      </div>

      <div className="mt-16 pb-8">
        <Link to="/" className="inline-flex items-center gap-2 text-sm text-muted-foreground hover:text-foreground transition-colors">
          <ArrowLeft size={16} /> Voltar
        </Link>
      </div>
    </div>
  </div>
));

PrivacyPolicy.displayName = "PrivacyPolicy";

export default PrivacyPolicy;
